/**
 * 3CX-Telefonanlagen-Adapter (CRM 2.0, v2 admin-scoped)
 *
 * feature/crm2-3cx-integration — Backend-Vertrag (alles im `{ data, meta,
 * errors }`-Envelope der v2-API, Guard: 'admin'):
 *
 *   GET   /v2/admin/3cx/active-call        — aktueller ringing/connected Call
 *                                             für die eigene Extension
 *                                             (data: null wenn keine Extension
 *                                             gesetzt oder kein aktiver Call)
 *   GET   /v2/admin/3cx/calls?user_id=     — Anrufhistorie (paginiert)
 *   POST  /v2/admin/3cx/click-to-call      — { number } → wählt von der
 *                                             eigenen Extension. 503 wenn die
 *                                             3CX-XAPI-Credentials noch nicht
 *                                             konfiguriert sind (siehe
 *                                             documentation/3cx-integration.md)
 *                                             → Aufrufer soll auf tel: zurückfallen.
 *   PATCH /v2/admin/me/three-cx-extension  — eigene Extension setzen/löschen
 *   GET   /v2/admin/3cx/recordings/{id}/audio-url — kurzlebiger (15 min)
 *                                             presigned Link zur Aufnahme
 *                                             (feature/3cx-recording-transcription)
 *
 * Die eigentliche Rufnummernsuche (/v2/3cx/lookup) läuft nur 3CX → Backend
 * (Webhook-Token-Auth) und wird vom Frontend nie aufgerufen.
 */

import { apiV2 } from './api';
import type { V2Meta, V2Paginated } from './familyAccounts';

/* ─── Domänen-Typen ─────────────────────────────────────────────────────── */

export type ThreeCxDirection = 'inbound' | 'outbound';
export type ThreeCxStatus = 'ringing' | 'connected' | 'ended' | 'missed';
export type ThreeCxContactType = 'pb' | 'bk';
/** Whisper-Transkription je Aufnahme (feature/3cx-recording-transcription). */
export type ThreeCxTranscriptionStatus = 'pending' | 'processing' | 'done' | 'failed';

export interface ThreeCxRecordingSummary {
  id: number;
  status: ThreeCxTranscriptionStatus;
  language: string | null;
  audio_seconds: number | null;
  recorded_at: string | null;
  transcribed_at: string | null;
}

export interface ThreeCxCall {
  id: number;
  call_id: string;
  direction: ThreeCxDirection;
  external_number: string | null;
  extension: string | null;
  status: ThreeCxStatus;
  started_at: string | null;
  answered_at: string | null;
  ended_at: string | null;
  duration_seconds: number | null;
  recording_url: string | null;
  user_id: number | null;
  contact_name: string | null;
  contact_type: ThreeCxContactType | null;
  /** Deep-link into CRM 2.0 (/pflege/{accountId}/{userId} or /betreuung/{userId}), null if unmatched. */
  profile_url: string | null;
  admin: string | null;
  /** Transkript aller Aufnahmen des Anrufs; null solange keins vorliegt. */
  transcript?: string | null;
  /** null = keine Aufnahme; sonst der „unfertigste" Status aller Aufnahmen. */
  transcription_status?: ThreeCxTranscriptionStatus | null;
  recordings?: ThreeCxRecordingSummary[];
}

/** Aktuell klingelnder Kundenanruf (Paket 2310d, Quelle: lookup-Webhook). */
export interface ThreeCxIncomingCall {
  id: number;
  user_id: number;
  external_number: string;
  contact_name: string | null;
  contact_type: ThreeCxContactType | null;
  profile_url: string | null;
  avatar_url: string | null;
  first_seen_at: string | null;
  last_seen_at: string | null;
}

/** Error shape thrown by clickToCall — narrows the axios error for callers. */
export interface ThreeCxApiError {
  status: number | undefined;
  code: string | null;
}

/* ─── Endpoints ──────────────────────────────────────────────────────────── */

export const v2ThreeCx = {
  /** GET /v2/admin/3cx/active-call — gepollt von CallPopup (alle 4s). */
  activeCall: async (): Promise<ThreeCxCall | null> => {
    const res = await apiV2.get<{ data: ThreeCxCall | null }>('/admin/3cx/active-call');
    return res.data.data;
  },

  /** GET /v2/admin/3cx/incoming-calls — Permission 3cx.anrufe.anzeigen, gepollt vom IncomingCallAlert. */
  incomingCalls: async (): Promise<ThreeCxIncomingCall[]> => {
    const res = await apiV2.get<{ data: ThreeCxIncomingCall[] | null }>('/admin/3cx/incoming-calls');
    return res.data.data ?? [];
  },

  /** GET /v2/admin/3cx/calls?user_id= — Anrufhistorie eines PB/BK. */
  calls: async (userId: number, params?: { page?: number; per_page?: number }): Promise<V2Paginated<ThreeCxCall>> => {
    const res = await apiV2.get<{ data: ThreeCxCall[]; meta: V2Meta }>('/admin/3cx/calls', {
      params: { user_id: userId, ...params },
    });
    return { data: res.data.data ?? [], meta: res.data.meta };
  },

  /**
   * POST /v2/admin/3cx/click-to-call — wirft bei 503 (3CX nicht konfiguriert)
   * oder 422 (keine eigene Extension gesetzt) einen ThreeCxApiError-artigen
   * Axios-Fehler; PhoneLink fängt das ab und fällt auf tel: zurück.
   */
  clickToCall: async (number: string): Promise<{ dialed: boolean }> => {
    const res = await apiV2.post<{ data: { dialed: boolean } }>('/admin/3cx/click-to-call', { number });
    return res.data.data;
  },

  /**
   * GET /v2/admin/3cx/recordings/{id}/audio-url — das <audio>-Element kann
   * keinen Bearer-Token senden, daher erst den presigned Link holen.
   */
  recordingAudioUrl: async (recordingId: number): Promise<{ url: string; expires_at: string; mime: string }> => {
    const res = await apiV2.get<{ data: { url: string; expires_at: string; mime: string } }>(
      `/admin/3cx/recordings/${recordingId}/audio-url`,
    );
    return res.data.data;
  },

  /** PATCH /v2/admin/me/three-cx-extension — Profil-Einstellungen. */
  updateExtension: async (extension: string | null): Promise<{ three_cx_extension: string | null }> => {
    const res = await apiV2.patch<{ data: { three_cx_extension: string | null } }>(
      '/admin/me/three-cx-extension',
      { three_cx_extension: extension },
    );
    return res.data.data;
  },
};

/** Extracts the v2 error code (e.g. "E_3CX_NOT_CONFIGURED") from an axios error, if present. */
export function threeCxErrorCode(err: unknown): string | null {
  const e = err as { response?: { data?: { errors?: Array<{ code?: string }> | null } } };
  return e.response?.data?.errors?.[0]?.code ?? null;
}

export function threeCxErrorStatus(err: unknown): number | undefined {
  const e = err as { response?: { status?: number } };
  return e.response?.status;
}
